import {
    CaretDown,
    Check,
    Plus
} from '@phosphor-icons/react';
import { useTranslation, type Translate } from '@client-app/i18n';
import clsx from 'clsx';
import {
    useEffect, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';

import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { Permission } from '@/contexts/AuthContext/permissions';
import { Workspace } from '@/contexts/AuthContext/useWorkspaces';
import {
    isExpiredWorkspaceTrialSubscription,
    isWorkspaceSubscriptionExpired,
    isWorkspaceSubscriptionTrial,
} from '@/contexts/AuthContext/workspaceSubscriptionUtils';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { resolveInvitePlanName } from '@/components/Settings/components/memberInviteAccess';
import { shouldUsePluginRestoreAutoRenewalCopy } from '@/components/Pricing/pluginRenewalCopy';
import { getAdminProductFeatures } from '@/utils/adminProductFeatures';
import { getCurrentProductCode, isPluginProduct } from '@common/api/productContext';

interface WorkspaceItemProps {
    workspace: Workspace;
    isActive: boolean;
    onClick?: () => void;
}

type WorkspacePlanDisplay = {
    planDisplayText: string;
    badge: string;
    isExpired: boolean;
    buttonText: string;
};

const CANONICAL_PLAN_NAMES = new Set(['solo', 'team']);

const normalizePlanName = (value?: string | null): string | null => {
    const normalized = value?.trim();
    return normalized && normalized.toLowerCase() !== 'plan expired' ? normalized : null;
};

const isCanonicalPlanName = (value?: string | null): value is string => (
    Boolean(value && CANONICAL_PLAN_NAMES.has(value.toLowerCase()))
);

const resolveWorkspacePlanName = ({
    planCode,
    planName,
    planLabel,
    statusLabel,
    fallback,
}: {
    planCode?: string | null;
    planName?: string | null;
    planLabel?: string | null;
    statusLabel?: string | null;
    fallback: string;
}): string => {
    const planNameFromTable = [planName, planLabel, statusLabel]
        .map(normalizePlanName)
        .find(Boolean) || null;
    const planNameFromCode = resolveInvitePlanName(planCode);

    return isCanonicalPlanName(planNameFromTable)
        ? planNameFromTable
        : planNameFromCode || planNameFromTable || fallback;
};

const translateWorkspaceAction = (
    value: string | null | undefined,
    t: Translate,
    isPluginWorkspace: boolean,
): string => {
    const normalized = (value || '').trim().toLowerCase();
    if (!normalized) return t('website.workspaceSwitcher.manage');

    switch (normalized) {
        case 'start subscription':
            return t('website.workspaceSwitcher.startSubscription');
        case 'upgrade':
            return t('website.workspaceSwitcher.upgrade');
        case 'renew':
            return shouldUsePluginRestoreAutoRenewalCopy({
                isPluginProduct: isPluginWorkspace,
                actionLabel: value,
            })
                ? t('website.pricing.pluginRestoreAutoRenewal')
                : t('website.planText.renew');
        case 'manage':
            return t('website.workspaceSwitcher.manage');
        default:
            return value || t('website.workspaceSwitcher.manage');
    }
};

// 辅助函数：从 workspace subscription 计算订阅状态
const getWorkspaceSubscriptionStatus = (
    subscription: Workspace['subscription'],
    t: Translate,
    isPluginWorkspace: boolean,
): WorkspacePlanDisplay => {
    if (!subscription) {
        return {
            planDisplayText: t('website.workspaceSwitcher.basic'),
            badge: '',
            isExpired: false,
            buttonText: t('website.workspaceSwitcher.manage'),
        };
    }

    const isTrial = isWorkspaceSubscriptionTrial(subscription);
    const isExpired = isWorkspaceSubscriptionExpired(subscription);
    const isExpiredTrial = isExpiredWorkspaceTrialSubscription(subscription);
    const isPendingCancellation = Boolean(
        subscription.cancel_at_period_end
        || subscription.status?.toLowerCase() === 'pending_cancellation',
    );

    const resolvedPlanName = resolveWorkspacePlanName({
        planCode: subscription.plan_code,
        planName: subscription.plan_name,
        planLabel: subscription.plan_label,
        statusLabel: subscription.status_label,
        fallback: t('website.workspaceSwitcher.basic'),
    });

    if (isExpired) {
        return {
            planDisplayText: resolvedPlanName,
            badge: t('website.workspaceSwitcher.expired'),
            isExpired: true,
            buttonText: isExpiredTrial
                ? t('website.workspaceSwitcher.startSubscription')
                : t('website.workspaceSwitcher.upgrade'),
        };
    }

    return {
        planDisplayText: resolvedPlanName,
        badge: isTrial ? t('website.workspaceSwitcher.trial') : (subscription.plan_badge || ''),
        isExpired: false,
        buttonText: isTrial && !isPendingCancellation
            ? t('website.workspaceSwitcher.startSubscription')
            : translateWorkspaceAction(
                subscription.action_label || subscription.next_action,
                t,
                isPluginWorkspace,
            ),
    };
};

const WorkspaceAvatar = ({
    workspace, className, fontSize = "text-sm" 
}: { workspace: Workspace, className?: string, fontSize?: string }) => {
    const [imageError, setImageError] = useState(false);
    
    // If logo_url exists and hasn't failed to load
    if (workspace.logo_url && !imageError) {
        return (
            <img
                src={workspace.logo_url}
                alt={workspace.name}
                className={clsx("w-full h-full object-contain p-0.5", className)}
                onError={() => setImageError(true)}
            />
        );
    }
    
    // Fallback to initial
    return (
        <div className={clsx("flex items-center justify-center w-full h-full font-bold text-gray-800 bg-gray-200", fontSize, className)}>
            {workspace.name?.charAt(0).toUpperCase() || 'W'}
        </div>
    );
};

const WorkspaceItem = ({
    workspace, isActive, onClick
}: WorkspaceItemProps) => {
    const { t } = useTranslation();
    const isPluginWorkspace = isPluginProduct(getCurrentProductCode());
    const { planDisplayText, badge, isExpired } = getWorkspaceSubscriptionStatus(
        workspace.subscription,
        t,
        isPluginWorkspace,
    );

    return (
        <div
            onClick={onClick}
            className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer group"
        >
            <div className="flex items-center gap-3 overflow-hidden min-w-0 flex-1">
                {/* Logo */}
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100 text-gray-700 shrink-0 overflow-hidden">
                    <WorkspaceAvatar workspace={workspace} fontSize="text-sm" />
                </div>

                {/* Name */}
                <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{workspace.name}</div>
                </div>

                {/* Plan Tag */}
                <div className="flex items-center gap-1 shrink-0">
                    <span className="px-1.5 py-0.5 bg-[#1A162F] text-white text-[10px] font-bold rounded uppercase">
                        {planDisplayText}
                    </span>
                    {badge ? (
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded uppercase ${
                            isExpired ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'
                        }`}>
                            {badge}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Check Icon */}
            {isActive ? <Check size={16} weight="bold" className="text-gray-900 shrink-0 ml-2" /> : null}
        </div>
    );
};

WorkspaceItem.displayName = 'WorkspaceItem';

interface WorkspaceSwitcherProps {
    collapsed: boolean;
    onSwitchWorkspace?: (workspaceId: string) => void | Promise<void>;
    onOpenSettingsTab?: (tab: string) => void;
    onCreateWorkspace?: () => void;
}

const WorkspaceSwitcher = ({
    collapsed,
    onSwitchWorkspace,
    onOpenSettingsTab,
    onCreateWorkspace,
}: WorkspaceSwitcherProps) => {
    const { t } = useTranslation();
    const {
        workspaces, currentWorkspace, switchWorkspace, userBilling, permissions, workspaceMembers
    } = useAuth();
    const { modal } = useAdminContext();
    const { startOnboarding } = useOnboarding();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // 检查是否有订阅管理权限
    const canManageSubscription = permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]);

    if (!workspaces || workspaces.length === 0) return null;

    const activeWorkspace = currentWorkspace || workspaces[0];
    const adminProductFeatures = getAdminProductFeatures();
    const shouldUsePluginWorkspaceUi = isPluginProduct(getCurrentProductCode());

    const subscriptionPlan = activeWorkspace.subscription
        ? getWorkspaceSubscriptionStatus(activeWorkspace.subscription, t, shouldUsePluginWorkspaceUi)
        : null;
    const billingPlanDisplayText = userBilling.planText.planDisplayText?.trim();
    const activeWorkspacePlanName = activeWorkspace.subscription
        ? resolveWorkspacePlanName({
            planCode: activeWorkspace.subscription.plan_code || userBilling.userPlan?.planCode,
            planName: activeWorkspace.subscription.plan_name,
            planLabel: activeWorkspace.subscription.plan_label,
            statusLabel: activeWorkspace.subscription.status_label,
            fallback: billingPlanDisplayText || t('website.workspaceSwitcher.basic'),
        })
        : null;
    const activePlanDisplayText = activeWorkspacePlanName
        || (shouldUsePluginWorkspaceUi ? t('website.workspaceSwitcher.basic') : billingPlanDisplayText);
    const userBillingButtonText = shouldUsePluginRestoreAutoRenewalCopy({
        isPluginProduct: shouldUsePluginWorkspaceUi,
        subscriptionStatus: userBilling.planText.status,
        actionLabel: userBilling.planText.buttonText,
    })
        ? t('website.pricing.pluginRestoreAutoRenewal')
        : userBilling.planText.buttonText;
    const activeWorkspacePlan = activePlanDisplayText
        ? {
            planDisplayText: activePlanDisplayText,
            badge: userBilling.planText.badge,
            isExpired: userBilling.planText.status === 'expired',
            buttonText: userBillingButtonText || subscriptionPlan?.buttonText || t('website.workspaceSwitcher.manage'),
        }
        : subscriptionPlan || {
            planDisplayText: userBilling.planText.planDisplayText || t('website.workspaceSwitcher.basic'),
            badge: userBilling.planText.badge,
            isExpired: userBilling.planText.status === 'expired',
            buttonText: userBillingButtonText || t('website.workspaceSwitcher.manage'),
        };
    const membersCount = workspaceMembers.members.length;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const openSettings = (tab: string) => {
        setIsOpen(false);
        if (onOpenSettingsTab) {
            onOpenSettingsTab(tab);
            return;
        }
        modal.openModal('profile', { activeTab: tab });
    };

    return (
        <div className={clsx("relative", collapsed && "w-fit mx-auto")} ref={menuRef}>
            {collapsed ? (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={activeWorkspace.name}
                    title={activeWorkspace.name}
                    className={clsx(
                        "w-10 h-10 flex items-center justify-center rounded-xl transition-colors border",
                        isOpen
                            ? "bg-white border-primary ring-1 ring-primary shadow-md"
                            : "bg-transparent border-transparent hover:border-gray-200 hover:bg-white text-gray-700"
                    )}
                >
                    <div className="w-6 h-6 flex items-center justify-center rounded overflow-hidden">
                        <WorkspaceAvatar workspace={activeWorkspace} className="w-full h-full object-contain" />
                    </div>
                </button>
            ) : (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={clsx(
                        "relative z-10 flex w-full items-center gap-2.5 rounded-[18px] border px-3.5 py-2 transition-all text-left group",
                        isOpen
                            ? "bg-white border-primary ring-1 ring-primary shadow-md"
                            : "bg-white border-gray-200 shadow-sm hover:border-gray-300"
                    )}
                >
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-700 shrink-0 border border-gray-100 group-hover:border-gray-200 transition-colors overflow-hidden">
                        <WorkspaceAvatar workspace={activeWorkspace} className="w-full h-full object-contain p-0.5" fontSize="text-xs" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="truncate text-[15px] font-medium text-gray-900">{activeWorkspace.name}</div>
                    </div>
                    <CaretDown size={11} weight="regular" className="text-gray-400 transition-colors group-hover:text-gray-600" />
                </button>
            )}

            {isOpen ? createPortal(
                <div
                    className="fixed w-[380px] max-w-[calc(100vw-24px)] mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 z-[9999] animate-in fade-in zoom-in-95 duration-100 overflow-hidden"
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                        top: menuRef.current?.getBoundingClientRect().bottom! + 4,
                        left: menuRef.current?.getBoundingClientRect().left
                    }}
                >
                    {/* Header: Current Workspace Info */}
                    <div className="p-4 pb-4">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-700 shrink-0 overflow-hidden shadow-sm">
                                <WorkspaceAvatar workspace={activeWorkspace} className="w-full h-full object-contain p-1" fontSize="text-base" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-base font-bold text-gray-900 truncate leading-tight mb-0.5">{activeWorkspace.name}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                    {t('sidebar.memberCount', {
                                        count: membersCount,
                                        plural: membersCount === 1 ? '' : 's',
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Upgrade Banner */}
                    <div className="mx-4 mb-4 flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate whitespace-nowrap text-sm font-semibold text-gray-900">
                                {activeWorkspacePlan.planDisplayText}
                            </span>
                            {activeWorkspacePlan.badge ? (
                                <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                    activeWorkspacePlan.isExpired ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'
                                }`}>
                                    {activeWorkspacePlan.badge}
                                </span>
                            ) : null}
                        </div>
                        {canManageSubscription && adminProductFeatures.pricingModal ? (
                            <button
                                onClick={() => {
                                    modal.openModal('pricing');
                                    setIsOpen(false);
                                }}
                                className={clsx(
                                    "shrink-0 whitespace-nowrap rounded-lg px-4 py-1.5 text-xs font-semibold shadow-sm transition-colors",
                                    activeWorkspacePlan.isExpired || activeWorkspacePlan.badge === 'Trial'
                                        ? "bg-[#1A162F] text-white hover:bg-[#1A162F]/90"
                                        : "bg-white border border-[#D5DBE7] text-[#3F495D] hover:bg-[#F8FAFC] shadow-[0_1px_2px_rgba(26,22,47,0.12)]"
                                )}
                            >
                                {activeWorkspacePlan.buttonText}
                            </button>
                        ) : null}
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gray-100 mx-0 mb-2" />

                    {/* All Workspaces Header */}
                    <div className="px-4 py-2 text-xs font-medium text-gray-500">
                        {t('sidebar.allWorkspaces')}
                    </div>

                    {/* Workspace List */}
                    <div className="px-2 pb-2 space-y-0.5 max-h-[200px] overflow-y-auto custom-scrollbar">
                        {workspaces.map(ws => (
                            <WorkspaceItem
                                key={ws.id}
                                workspace={ws}
                                isActive={ws.id === currentWorkspace?.id}
                                onClick={() => {
                                    if (onSwitchWorkspace) {
                                        void onSwitchWorkspace(ws.id);
                                    } else {
                                        switchWorkspace(ws.id);
                                    }
                                    setIsOpen(false);
                                }}
                            />
                        ))}
                    </div>

                    {/* Footer: Create Workspace */}
                    <div className="p-2 border-t border-gray-100 mt-1">
                        <button
                            onClick={() => {
                                if (onCreateWorkspace) {
                                    onCreateWorkspace();
                                } else {
                                    startOnboarding('workspace_switcher');
                                }
                                setIsOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-gray-600 hover:text-gray-900 group"
                        >
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 border border-gray-200 border-dashed shrink-0 text-gray-400 group-hover:text-gray-600 group-hover:border-gray-400 transition-colors">
                                <Plus size={16} weight="bold" />
                            </div>
                            <span className="text-sm font-medium">{t('sidebar.createWorkspace')}</span>
                        </button>
                    </div>
                </div>,
                document.body
            ) : null}
        </div>
    );
};

export default WorkspaceSwitcher;
